import { StackNavigationProp, StackScreenProps } from "@react-navigation/stack";
import { ScrollView, StyleSheet, View } from "react-native";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { MissionAction, useMissionHandler } from "../../hooks/missions";
import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractState, TerminateReason } from "../../__generated__/graphql";
import { CompositeNavigationProp, useNavigation } from "@react-navigation/native";
import { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import { WorkerTabParamList } from "../../navigations/worker/Home";
import { ClientTabParamList } from "../../navigations/client/Home";
import { GET_MISSION, GET_TERMINATION_INFO } from "../../graphql/core";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { SwipeButton } from "../../components/SwipeButton";
import { useSnackbar } from "../../hooks/snackbar";
import { useTracking } from "../../hooks/tracking";
import TextField from "../../components/TextField";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import Button from "../../components/Button";
import MarkdownView from "../../components/MarkdownView";
import { useIntercom } from "../../hooks/intercom";

type MissionTerminationProps = StackScreenProps<RootStackParamList, "MissionTermination">;

type LocalNavigatorProps = CompositeNavigationProp<
    StackNavigationProp<RootStackParamList, "Home">,
    CompositeNavigationProp<
        BottomTabNavigationProp<WorkerTabParamList, "MissionsScreen">,
        BottomTabNavigationProp<ClientTabParamList, "MissionsScreen">
    >
>;

const MissionTerminationScreen = ({ route }: MissionTerminationProps) => {
    const [otherReason, setOtherReason] = useState<string>("");
    const navigation = useNavigation<LocalNavigatorProps>();
    const i18n = useI18n();
    const missionId = route?.params?.missionId || "test-mission-id";
    const { profileType } = React.useContext(ProfileSwitcherManagerContext);
    const { trackEvent } = useTracking();

    const {
        data,
        error: getError,
        loading,
    } = useQuery(GET_MISSION, {
        variables: { id: missionId },
    });
    const {
        data: termInfo,
        error: getInfoError,
        loading: getInfoLoading,
    } = useQuery(GET_TERMINATION_INFO, {
        variables: { missionId },
    });

    const goAfterAction = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.replace("Home", {
                screen: "MissionsScreen",
                params: { screen: "MissionList" },
            });
        }
    }, [navigation]);
    const { showSnackbar } = useSnackbar();

    const onActionSuccess = useCallback(
        (actionName: MissionAction) => {
            void trackEvent("click_confirm_terminate_mission");

            if (actionName == "close") {
                goAfterAction();
                return;
            }
            let text = i18n.t(`screens.termination.success_info.${actionName}`);

            if (actionName === "terminate") {
                // we need to keep the old translation for app compatibility
                text = i18n.t(`screens.termination.success_info.request`);
            }
            showSnackbar({
                text,
                mode: "info",
            });
            navigation.popToTop();
        },
        [goAfterAction, i18n, navigation, showSnackbar, trackEvent],
    );

    const {
        onAction,
        processingAction,
        error: procError,
        reset,
    } = useMissionHandler(onActionSuccess);
    const onClearError = useCallback(() => {
        reset();
        navigation.replace("Home", { screen: "MissionsScreen", params: { screen: "MissionList" } });
    }, [navigation, reset]);

    const mission = data?.getMission || null;
    const error = useMemo(() => {
        if (procError) {
            return i18n.t(`screens.termination.action_errors.${processingAction || "default"}`);
        }
        if (getError) {
            return i18n.t(`screens.termination.get_errors`);
        }
        if (getInfoError) {
            return i18n.t(`screens.termination.get_info_errors`);
        }
        return null;
    }, [procError, getInfoError, getError, i18n, processingAction]);

    const styles = useStyleSheet();

    const newState = termInfo?.getTerminationInfo?.newState;
    const cancellation = termInfo?.getTerminationInfo?.cancellationRequest;
    const { presentIntercom } = useIntercom();

    const buttonTitle = useMemo(() => {
        if (
            cancellation ||
            newState === ContractState.Terminated ||
            newState === ContractState.Cancelled
        ) {
            return i18n.t("screens.termination.actions.request");
        }
        return i18n.t(`screens.termination.actions.close`);
    }, [i18n, newState, cancellation]);

    const buttonAction = useMemo(() => {
        if (cancellation) {
            return "close";
        }
        if (newState === ContractState.Terminated || newState === ContractState.Cancelled) {
            return "terminate";
        }
        return "close";
    }, [newState, cancellation]);

    const missingDetails = useMemo(() => {
        if (!otherReason) {
            return i18n.t("screens.termination.missing_other_reason");
        }
        return undefined;
    }, [i18n, otherReason]);

    const onSupportPressed = useCallback(() => {
        void presentIntercom();
    }, [presentIntercom]);

    return (
        <>
            <LoadingBoundary
                loading={loading || getInfoLoading}
                error={error}
                clearError={onClearError}
            >
                <ScrollView>
                    {cancellation ? (
                        <VStack gap={1} style={styles.container} top={1}>
                            {cancellation.isCancelledBySelf ? (
                                <MarkdownView
                                    markdown={i18n.t(
                                        `screens.termination.callouts.already_requested.self`,
                                    )}
                                />
                            ) : (
                                <MarkdownView
                                    markdown={i18n.t(
                                        `screens.termination.callouts.already_requested.other`,
                                    )}
                                />
                            )}
                        </VStack>
                    ) : (
                        mission && (
                            <VStack gap={1} style={styles.container} top={1}>
                                <MarkdownView
                                    markdown={i18n.t(
                                        `screens.termination.callouts.${profileType}.request`,
                                    )}
                                />
                                <TextField
                                    multiline
                                    value={otherReason}
                                    onChangeText={setOtherReason}
                                    label={i18n.t(`screens.termination.reason_label`)}
                                    error={missingDetails}
                                />
                            </VStack>
                        )
                    )}
                </ScrollView>
                <View style={styles.containerActions}>
                    {cancellation ? (
                        <Button
                            buttonMode="secondary"
                            label={i18n.t("screens.termination.actions.support")}
                            onPress={onSupportPressed}
                            size="large"
                        />
                    ) : (
                        <SwipeButton
                            testID="button-terminate-mission"
                            disabled={!mission || !newState || !!missingDetails}
                            processing={!!processingAction}
                            title={buttonTitle}
                            onSwipeSuccess={() => {
                                if (mission) {
                                    void onAction(
                                        buttonAction,
                                        mission,
                                        TerminateReason.Other,
                                        new Date(),
                                        otherReason,
                                    );
                                }
                            }}
                            thumbIconName="swipe-right"
                            mode="danger"
                        />
                    )}
                </View>
            </LoadingBoundary>
        </>
    );
};

const useStyleSheet = () => {
    const { gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingHorizontal: gridSize,
                },
                containerActions: {
                    marginBottom: gridSize,
                    paddingHorizontal: gridSize,
                    position: "relative",
                    alignContent: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: "100%",
                    alignItems: "center",
                },
            }),
        [gridSize],
    );
};
export default MissionTerminationScreen;
